/**
 * SCH-5.9 — the logic behind the Insights tab, extracted so it is actually tested.
 *
 * WHY THIS IS A SEPARATE MODULE
 * -----------------------------
 * vitest is configured with `include: ['src/**\/*.test.ts']` and `environment: 'node'`, and
 * neither `jsdom` nor `@testing-library/react` is installed. A `.test.tsx` beside the component
 * would not be collected, and could not render if it were — which is exactly what happened to
 * ConnectionTab.test.tsx, FieldMappingTab.test.tsx and SyncStatusCard.test.tsx, three files that
 * look like coverage and have never executed a line.
 *
 * So everything here that can be quietly wrong — the weighting rule, the null handling, the state
 * machine the tab renders from — lives in plain functions in a plain `.ts` module, and the
 * component is a thin shell over them. Coverage that runs beats coverage that reads well.
 */

import type {
  SyncMetricsBucketSize,
  SyncMetricsPoint,
  SyncMetricsRange,
} from '@/types/integrations/syncMetrics';

export const RANGE_LABELS: Record<SyncMetricsRange, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
};

/** 90 days of daily points is a wall of ink at this width; weeks read better. */
export function bucketForRange(range: SyncMetricsRange): SyncMetricsBucketSize {
  return range === '90d' ? 'week' : 'day';
}

export function formatBucket(iso: string, bucket: SyncMetricsBucketSize): string {
  try {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    const label = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    return bucket === 'week' ? `w/c ${label}` : label;
  } catch {
    return iso;
  }
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const seconds = ms / 1000;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function formatPercent(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 1000) / 10}%`;
}

export interface Headline {
  successRate: number | null;
  avgDurationMs: number | null;
  projectsChanged: number;
  authFailures: number;
  totalRuns: number;
}

/**
 * Overall figures for the range.
 *
 * Success rate is weighted by RUNS, not the mean of the per-bucket rates. Averaging rates gives a
 * day with one run the same say as a day with fifty, and forces a decision about what an empty
 * bucket's absent rate contributes — a question with no good answer. Summing runs sidesteps both
 * and is the number an operator actually means by "how often does the sync work".
 *
 * Empty buckets contribute nothing at all: they have no rate and no duration, so they neither
 * drag the average down nor prop it up.
 */
export function summarize(points: SyncMetricsPoint[]): Headline {
  let totalRuns = 0;
  let successfulRuns = 0;
  let durationWeighted = 0;
  let durationRuns = 0;
  let projectsChanged = 0;
  let authFailures = 0;

  for (const point of points) {
    totalRuns += point.totalRuns;
    successfulRuns += point.successfulRuns;
    projectsChanged += point.projectsChanged;
    authFailures += point.authFailures;
    if (point.avgDurationMs !== null && point.totalRuns > 0) {
      durationWeighted += point.avgDurationMs * point.totalRuns;
      durationRuns += point.totalRuns;
    }
  }

  return {
    totalRuns,
    successRate: totalRuns > 0 ? successfulRuns / totalRuns : null,
    avgDurationMs: durationRuns > 0 ? Math.round(durationWeighted / durationRuns) : null,
    projectsChanged,
    authFailures,
  };
}

export interface ChartPoint extends SyncMetricsPoint {
  label: string;
  /** Percent for the axis. NULL STAYS NULL so recharts breaks the line. */
  successPercent: number | null;
  /** Seconds for the axis. NULL STAYS NULL. */
  durationSeconds: number | null;
}

/**
 * Series points for recharts.
 *
 * The null passes through untouched. A bucket with no runs has no success rate, and a 0 here
 * would draw the line to the floor and read as a total outage on a day when nothing was
 * scheduled — the alarm this tab exists to raise, raised falsely. Paired with
 * `connectNulls={false}` on the Line, null renders as a gap, which is the truth.
 */
export function toChartPoints(
  points: SyncMetricsPoint[],
  bucket: SyncMetricsBucketSize,
): ChartPoint[] {
  return points.map((point) => ({
    ...point,
    label: formatBucket(point.bucketStart, bucket),
    successPercent: point.successRate === null ? null : point.successRate * 100,
    durationSeconds:
      point.avgDurationMs === null ? null : Math.round(point.avgDurationMs / 1000),
  }));
}

export type InsightsViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty' }
  | { kind: 'ready'; points: SyncMetricsPoint[] };

interface ViewStateInput {
  isLoading: boolean;
  queryError: unknown;
  result?: {
    data: { data: SyncMetricsPoint[] } | null;
    error: { code: string; message: string } | null;
  } | null;
}

/**
 * Which of the four states the tab is in.
 *
 * An empty series is `empty`, never `error`: a quiet period is a legitimate answer and the
 * endpoint returns it as data. Conflating the two would tell an operator their observability is
 * broken every time their integration was simply idle.
 *
 * An envelope error outranks an empty series — otherwise a failed request whose data is null
 * would render as a reassuring "no activity".
 */
export function insightsViewState({
  isLoading,
  queryError,
  result,
}: ViewStateInput): InsightsViewState {
  if (isLoading) return { kind: 'loading' };

  if (result?.error) {
    return { kind: 'error', message: result.error.message || 'Failed to load sync insights.' };
  }
  if (queryError) {
    return {
      kind: 'error',
      message:
        queryError instanceof Error ? queryError.message : 'Failed to load sync insights.',
    };
  }

  const points = result?.data?.data ?? [];
  if (points.length === 0) return { kind: 'empty' };
  return { kind: 'ready', points };
}

/** The auth-failure banner copy, or null when there is nothing to warn about. */
export function authFailureWarning(
  headline: Headline,
  range: SyncMetricsRange,
): string | null {
  if (headline.authFailures <= 0) return null;
  const noun = headline.authFailures === 1 ? 'run' : 'runs';
  return (
    `${headline.authFailures} ${noun} paused on an authentication failure in the last ` +
    `${RANGE_LABELS[range]}. Check the API key on the Connection tab.`
  );
}
